import type { StoqSettings } from '../types'
import type { ModalStyles, ModalTexts, ResolvedModalStyles } from './types'

/**
 * Fallbacks used when neither the shop settings nor a per-call override
 * provides a value. Settings normally win — a fresh shop has every
 * `storefront_form_*` field populated with its own defaults.
 */
export const DEFAULT_TEXTS: ModalTexts = {
  header: 'Get notified when back in stock',
  description: 'We will send you a notification as soon as this product is available again.',
  emailLabel: 'Email',
  emailPlaceholder: 'Your email address',
  phoneLabel: 'Phone',
  phonePlaceholder: 'Your phone number',
  buttonText: 'Notify me',
  success: 'You are now subscribed! We will notify you as soon as this product is available again.',
  optinSuccess: 'Almost there — check your inbox and confirm your email to complete the signup.',
  emailError: 'Please enter a valid email address.',
  phoneError: 'Please enter a valid phone number.',
  duplicateError: 'You are already subscribed for this product.',
  emptyError: 'Please enter your contact information.',
  genericError:
    'We encountered an issue processing your request. Please try again or contact support if the problem persists.',
  terms: '',
  closeLabel: 'Close',
}

export const DEFAULT_STYLES: ResolvedModalStyles = {
  textColor: '#202223',
  backgroundColor: '#ffffff',
  borderRadius: '8px',
  buttonTextColor: '#ffffff',
  buttonBackgroundColor: '#000000',
  fontFamily: 'inherit',
  zIndex: '2147483000',
}

/** Map of ModalTexts key → settings key (SettingExtensionSerializer). */
const TEXT_SETTING_KEYS: Partial<Record<keyof ModalTexts, string>> = {
  header: 'storefront_form_header',
  description: 'storefront_form_description',
  emailLabel: 'storefront_form_email_label',
  emailPlaceholder: 'storefront_form_email_placeholder',
  phoneLabel: 'storefront_form_phone_label',
  phonePlaceholder: 'storefront_form_phone_placeholder',
  buttonText: 'storefront_form_button_text',
  success: 'storefront_form_success',
  optinSuccess: 'optin_success_text',
  emailError: 'storefront_form_error',
  phoneError: 'storefront_form_phone_error',
  duplicateError: 'storefront_form_duplicate_error',
  emptyError: 'storefront_form_empty_error',
  terms: 'storefront_form_terms',
}

function stringSetting(settings: Readonly<StoqSettings>, key: string): string | undefined {
  const value = settings[key]
  return typeof value === 'string' && value.trim() !== '' ? value : undefined
}

/**
 * Resolves the modal texts: per-call override → shop setting → built-in
 * default. Blank-string settings are treated as unset.
 */
export function resolveTexts(
  settings: Readonly<StoqSettings>,
  overrides?: Partial<ModalTexts>,
): ModalTexts {
  const texts = {} as ModalTexts
  for (const key of Object.keys(DEFAULT_TEXTS) as Array<keyof ModalTexts>) {
    const override = overrides?.[key]
    const settingKey = TEXT_SETTING_KEYS[key]
    texts[key] = override
      ?? (settingKey !== undefined ? stringSetting(settings, settingKey) : undefined)
      ?? DEFAULT_TEXTS[key]
  }
  // `terms` may legitimately be blank — an explicit empty override hides it.
  if (overrides?.terms !== undefined) texts.terms = overrides.terms
  return texts
}

/**
 * Success message for a completed signup. When the shop requires double
 * opt-in (`optin_required`) and an email intent was created, the opt-in
 * confirmation copy is shown instead of the regular success message.
 */
export function successMessage(
  texts: ModalTexts,
  settings: Readonly<StoqSettings>,
  usedEmail: boolean,
): string {
  if (usedEmail && settings.optin_required === true) return texts.optinSuccess
  return texts.success
}

/**
 * Mirrors the embed: a configured font gets web-safe fallbacks appended;
 * `inherit` is passed through so the modal blends into the theme.
 */
function resolveFontFamily(settings: Readonly<StoqSettings>): string | undefined {
  const font = stringSetting(settings, 'storefront_font_family')
  if (font === undefined) return undefined
  return font === 'inherit' ? font : `${font}, Helvetica, sans-serif`
}

function borderRadiusSetting(settings: Readonly<StoqSettings>): string | undefined {
  const value = settings['storefront_form_border_radius']
  if (typeof value === 'number' && Number.isFinite(value)) return `${value}px`
  if (typeof value === 'string' && value.trim() !== '') return `${parseFloat(value) || 0}px`
  return undefined
}

/**
 * Resolves the modal styles: per-call override → shop setting → built-in
 * default.
 */
export function resolveStyles(
  settings: Readonly<StoqSettings>,
  overrides?: ModalStyles,
): ResolvedModalStyles {
  return {
    textColor: overrides?.textColor
      ?? stringSetting(settings, 'storefront_form_text_color')
      ?? DEFAULT_STYLES.textColor,
    backgroundColor: overrides?.backgroundColor
      ?? stringSetting(settings, 'storefront_form_background_color')
      ?? DEFAULT_STYLES.backgroundColor,
    borderRadius: overrides?.borderRadius
      ?? borderRadiusSetting(settings)
      ?? DEFAULT_STYLES.borderRadius,
    buttonTextColor: overrides?.buttonTextColor
      ?? stringSetting(settings, 'storefront_form_button_text_color')
      ?? DEFAULT_STYLES.buttonTextColor,
    buttonBackgroundColor: overrides?.buttonBackgroundColor
      ?? stringSetting(settings, 'storefront_form_button_background_color')
      ?? DEFAULT_STYLES.buttonBackgroundColor,
    fontFamily: overrides?.fontFamily
      ?? resolveFontFamily(settings)
      ?? DEFAULT_STYLES.fontFamily,
    zIndex: overrides?.zIndex ?? DEFAULT_STYLES.zIndex,
  }
}
